import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Lock, 
  Mail, 
  Key, 
  ShieldCheck, 
  ArrowRight, 
  AlertCircle, 
  Smartphone,
  CheckCircle2
} from 'lucide-react';
import { useStore } from '../store/useStore';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useStore();
  const [email, setEmail] = useState('alex.mitchell@sydney-design.com.au');
  const [password, setPassword] = useState('••••••••••••••••');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfaStep, setShowMfaStep] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInitialSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setIsLoading(true);
    // Simulate MFA prompt
    setTimeout(() => {
      setIsLoading(false);
      setShowMfaStep(true);
    }, 500);
  };

  const handleMfaVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      const isAdmin = email.toLowerCase().includes('admin');
      login(email.trim(), isAdmin);
      setIsLoading(false);
      navigate(isAdmin ? '/admin' : '/dashboard');
    }, 600);
  };

  const handleSocialLogin = (provider: 'Google' | 'Apple') => {
    setIsLoading(true);
    setTimeout(() => {
      login(`demo.${provider.toLowerCase()}@user.au`, false);
      setIsLoading(false);
      navigate('/dashboard');
    }, 600);
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto shadow-lg shadow-emerald-950/40">
          <ShieldCheck className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white">Sign In to IDFraud.au</h1>
        <p className="text-xs text-slate-400">Your Australian personal identity security centre</p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
        {error && (
          <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-200 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!showMfaStep ? (
          <form onSubmit={handleInitialSubmit} className="space-y-4 text-xs">
            <div className="space-y-1">
              <label className="block font-semibold text-slate-300">Email Address:</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="alex.mitchell@sydney-design.com.au"
                  required
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="block font-semibold text-slate-300">Master Password:</label>
                <Link to="/forgot-password" className="text-emerald-400 hover:underline text-[11px]">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-bold text-xs shadow-lg shadow-emerald-950/40 transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? 'Verifying...' : 'Continue with Password'}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>

            {/* Social Logins */}
            <div className="pt-2 border-t border-slate-800 space-y-2">
              <div className="text-center text-[11px] text-slate-500 font-medium">Or continue with single sign-on:</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleSocialLogin('Google')}
                  className="py-2 px-3 rounded-xl bg-slate-950 border border-slate-700 hover:border-slate-600 text-slate-200 font-medium text-xs flex items-center justify-center gap-2 transition-colors"
                >
                  <span>Google</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialLogin('Apple')}
                  className="py-2 px-3 rounded-xl bg-slate-950 border border-slate-700 hover:border-slate-600 text-slate-200 font-medium text-xs flex items-center justify-center gap-2 transition-colors"
                >
                  <span>Apple ID</span>
                </button>
              </div>
            </div>
          </form>
        ) : (
          /* MFA Verification Screen */
          <form onSubmit={handleMfaVerify} className="space-y-4 text-xs animate-fade-in">
            <div className="text-center space-y-1 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <Smartphone className="w-6 h-6 text-emerald-400 mx-auto" />
              <div className="font-bold text-white text-sm">Two-Factor Authentication</div>
              <p className="text-[11px] text-slate-400">
                Enter the 6-digit security code from your Authenticator app (e.g. 2FAS, Google Authenticator) for <span className="text-slate-300 font-mono">{email}</span>.
              </p>
            </div>

            <div className="space-y-1">
              <label className="block font-semibold text-slate-300">6-Digit Code (Demo: any 6 digits):</label>
              <input
                type="text"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="492810"
                autoFocus
                className="w-full text-center py-3 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-lg tracking-widest focus:outline-none focus:border-emerald-500"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-emerald-950/40 transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? 'Verifying MFA...' : 'Authenticate & Sign In'}
            </button>

            <button
              type="button"
              onClick={() => setShowMfaStep(false)}
              className="w-full text-center text-[11px] text-slate-400 hover:text-white"
            >
              ← Back to login options
            </button>
          </form>
        )}

        <div className="text-center text-xs text-slate-400 pt-2 border-t border-slate-800">
          Don't have an account yet?{' '}
          <Link to="/register" className="text-emerald-400 hover:underline font-bold">
            Create Free Account
          </Link>
        </div>
      </div>
    </div>
  );
};
